import type { PhaseTemplate, TaskTemplate, VehicleNodes, FunctionalTask } from '@/types';

const DEFAULT_VEHICLE_NODES = ['VPI', 'VDR', 'S', 'SORP', 'APPV'];

const DEFAULT_VEHICLE_NODES_PATTERN = /^(VPI|VDR|S|SORP|APPV)([+-]\d+)周?$/;

// SGM 车灯项目默认模板
// 严格按照 PRD 文件定义
export const sgmDefaultTemplate: PhaseTemplate[] = [
  {
    name: '阶段1：商务定点阶段（Phase 1：Business Sourcing）',
    phase: 'supplierSelection',
    tasks: [
      {
        name: 'SOR',
        duration: 28,
        minDuration: 28,
        startNode: '项目立项',
        subTask: 'Supplier Sourcing',
        description: '需求发布',
      },
      {
        name: 'TR',
        duration: 7,
        minDuration: 7,
        predecessor: 'SOR',
        subTask: 'Supplier Sourcing',
        description: '技术评审',
      },
      {
        name: 'Quotation',
        duration: 63,
        minDuration: 56,
        predecessor: 'TR',
        subTask: 'Supplier Sourcing',
        description: '报价',
      },
      {
        name: 'Tooling Audit',
        duration: 14,
        minDuration: 14,
        predecessor: 'Quotation',
        subTask: 'Supplier Sourcing',
        description: '模具评审',
        reviewTask: true,
      },
      {
        name: 'AC',
        duration: 14,
        minDuration: 14,
        predecessor: 'Tooling Audit',
        subTask: 'Supplier Sourcing',
        description: '定点完成',
      },
    ],
  },
  {
    name: '阶段2：造型设计阶段（Phase 2：Styling Concept）',
    phase: 'stylingConcept',
    tasks: [
      {
        name: 'Lamp Styling Dev',
        duration: null,
        minDuration: null,
        startNode: '项目立项',
        subTask: 'Lamp Styling',
        description: '造型概念设计（工期N/A，动态计算）',
      },
      {
        name: 'Lit-Mockup Sourcing',
        duration: 14,
        minDuration: 14,
        predecessor: 'Lamp Styling Dev',
        subTask: 'Lamp Styling',
        description: '点灯样件定点',
      },
      {
        name: 'Lit-Mockup Manufactory',
        duration: 28,
        minDuration: 21,
        predecessor: 'Lit-Mockup Sourcing',
        endNode: '数据发布',
        offsetWeeks: 2,
        subTask: 'Lamp Styling',
        description: '点灯样件加工',
      },
    ],
  },
  {
    name: '阶段3：工程设计阶段（Phase 3：Engineering Design）',
    phase: 'detailedDesign',
    tasks: [
      {
        name: 'Engineering Feasiblity',
        duration: null,
        minDuration: null,
        startNode: 'AC',
        endNode: '数据发布',
        subTask: 'Structure Design',
        description: '工程可行性分析（工期N/A，由AC到VDR动态计算）',
      },
      {
        name: 'P2 Data Dev',
        duration: 56,
        minDuration: 56,
        startNode: '数据发布',
        subTask: 'Structure Design',
        description: 'P2数据开发',
      },
      {
        name: 'A-Sample Dev',
        duration: 42,
        minDuration: 28,
        endNode: 'Lit-Mockup Manufactory',
        subTask: 'E/E Design',
        description: 'A样开发',
      },
      {
        name: 'B-sample Design',
        duration: 21,
        minDuration: 14,
        startNode: '数据发布',
        offsetWeeks: 8,
        subTask: 'E/E Design',
        description: 'B样设计',
      },
      {
        name: 'PCBA Sourcing',
        duration: 21,
        minDuration: 14,
        predecessor: 'B-sample Design',
        subTask: 'E/E Design',
        description: 'PCBA定点',
      },
      {
        name: 'B-sample Validation',
        duration: 42,
        minDuration: 28,
        predecessor: 'PCBA Sourcing',
        subTask: 'E/E Design',
        description: 'B样验证',
      },
      {
        name: 'C-sample Design',
        duration: 14,
        minDuration: 14,
        predecessor: 'B-sample Validation',
        subTask: 'E/E Design',
        description: 'C样设计',
      },
    ],
  },
  {
    name: '阶段4：Tooling开发阶段（Phase 4：Tooling Development）',
    phase: 'toolingLine',
    tasks: [
      {
        name: '模具定点',
        duration: 56,
        minDuration: 56,
        startNode: '数据发布',
        offsetWeeks: -4,
        subTask: 'Mold Development',
        description: '模具供应商定点',
      },
      {
        name: '模具设计',
        duration: 42,
        minDuration: 28,
        predecessor: '模具定点',
        overlapWeeks: 4,
        subTask: 'Mold Development',
        description: '模具设计',
      },
      {
        name: 'TKO评审',
        duration: 28,
        minDuration: 21,
        predecessor: '模具设计',
        overlapWeeks: 2,
        subTask: 'Mold Development',
        description: '模具设计评审',
        reviewTask: true,
      },
      {
        name: '模具加工',
        duration: 112,
        minDuration: 84,
        predecessor: 'TKO评审',
        overlapWeeks: 2,
        subTask: 'Mold Development',
        description: '模具加工',
      },
      {
        name: '模具调试转移',
        duration: 84,
        minDuration: 56,
        predecessor: '模具加工',
        subTask: 'Mold Development',
        description: '模具调试转移',
      },
      {
        name: '检具定点',
        duration: 28,
        minDuration: 28,
        startNode: '数据发布',
        offsetWeeks: 4,
        subTask: 'Gauge Development',
        description: '检具供应商定点',
      },
      {
        name: '检具设计',
        duration: 28,
        minDuration: 28,
        predecessor: '检具定点',
        subTask: 'Gauge Development',
        description: '检具设计',
      },
      {
        name: '检具评审',
        duration: 14,
        minDuration: 14,
        predecessor: '检具设计',
        subTask: 'Gauge Development',
        description: '检具设计评审',
      },
      {
        name: '检具加工',
        duration: 56,
        minDuration: 56,
        predecessor: '检具评审',
        subTask: 'Gauge Development',
        description: '检具加工',
      },
      {
        name: '检具标定转移',
        duration: 28,
        minDuration: 28,
        predecessor: '检具加工',
        subTask: 'Gauge Development',
        description: '检具标定转移',
      },
      {
        name: '产线定点',
        duration: 28,
        minDuration: 28,
        startNode: '数据发布',
        subTask: 'Line Development',
        description: '装配线供应商定点',
      },
      {
        name: '产线设计',
        duration: 42,
        minDuration: 28,
        predecessor: '产线定点',
        subTask: 'Line Development',
        description: '装配线设计',
      },
      {
        name: '产线评审',
        duration: 7,
        minDuration: 7,
        predecessor: '产线设计',
        subTask: 'Line Development',
        description: '装配线设计评审',
      },
      {
        name: '工装设备',
        duration: 126,
        minDuration: 70,
        predecessor: '产线评审',
        subTask: 'Line Development',
        description: '工装设备制造',
      },
      {
        name: '产线组装',
        duration: 28,
        minDuration: 14,
        predecessor: '工装设备',
        subTask: 'Line Development',
        description: '装配线组装',
      },
      {
        name: '产线调试',
        duration: 56,
        minDuration: 42,
        predecessor: '产线组装',
        subTask: 'Line Development',
        description: '装配线调试',
      },
    ],
  },
  {
    name: '阶段5：产品开发阶段（Phase 5：Product Development）',
    phase: 'productDevelopment',
    tasks: [
      {
        name: '总成T0',
        duration: 21,
        minDuration: 14,
        startNode: '模具加工',
        subTask: 'Production Trial',
        description: '模具件首次试制',
        functionalTask: '产品T0',
      },
      {
        name: '总成T1',
        duration: 28,
        minDuration: 21,
        predecessor: '总成T0',
        subTask: 'Production Trial',
        description: '第一次工程样件',
      },
      {
        name: '总成T2',
        duration: 28,
        minDuration: 21,
        predecessor: '总成T1',
        subTask: 'Production Trial',
        description: '第二次工程样件',
      },
      {
        name: '总成T3',
        duration: 35,
        minDuration: 28,
        predecessor: '总成T2',
        subTask: 'Production Trial',
        description: '第三次工程样件',
      },
      {
        name: 'GIF认可',
        duration: 84,
        minDuration: 56,
        startNode: '总成T0',
        subTask: 'AAR Development',
        description: 'GIF外观认可',
      },
      {
        name: '皮纹加工',
        duration: 28,
        minDuration: 14,
        predecessor: 'GIF认可',
        subTask: 'AAR Development',
        description: '皮纹加工',
        functionalTask: '皮纹加工',
      },
      {
        name: '皮纹认可',
        duration: 42,
        minDuration: 28,
        predecessor: '皮纹加工',
        subTask: 'AAR Development',
        description: '皮纹外观认可',
      },
      {
        name: '外观认可',
        duration: 42,
        minDuration: 28,
        predecessor: '皮纹认可',
        overlapWeeks: 4,
        subTask: 'AAR Development',
        description: '最终外观认可',
      },
      {
        name: '80%合格率',
        duration: 70,
        minDuration: 56,
        startNode: '总成T0',
        subTask: 'Dimension Development',
        description: '尺寸80%合格率达成',
      },
      {
        name: '90%合格率',
        duration: 56,
        minDuration: 56,
        predecessor: '80%合格率',
        subTask: 'Dimension Development',
        description: '尺寸90%合格率达成',
      },
      {
        name: 'ADVP Sign-off',
        duration: 28,
        minDuration: 21,
        subTask: 'E/E Test',
        description: 'ADVP签核',
      },
      {
        name: 'Lab Sourcing',
        duration: 28,
        minDuration: 28,
        predecessor: 'ADVP Sign-off',
        subTask: 'E/E Test',
        description: '实验室定点',
      },
      {
        name: 'C-sample Manufactory',
        duration: 56,
        minDuration: 42,
        startNode: 'C-sample Design',
        subTask: 'E/E Test',
        description: 'C样制作',
      },
      {
        name: 'Initial Test',
        duration: 14,
        minDuration: 14,
        predecessor: 'C-sample Manufactory',
        subTask: 'E/E Test',
        description: '初始测试',
      },
      {
        name: 'EMC/3172',
        duration: 70,
        minDuration: 56,
        predecessor: 'Initial Test',
        subTask: 'E/E Test',
        description: 'EMC和3172测试',
        functionalTask: '电子试验',
      },
      {
        name: 'GMW14906',
        duration: 98,
        minDuration: 91,
        startNode: '总成T0',
        subTask: 'Lamp Test',
        description: 'GMW14906灯具测试',
        functionalTask: '产品试验',
      },
      {
        name: '强检/CCC',
        duration: 56,
        minDuration: 42,
        startNode: '总成T2',
        subTask: 'Regulations',
        description: '法规认证测试',
        functionalTask: '法规认证',
      },
    ],
  },
];

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

function parseVehicleNode(nodeName: string, vehicleNodes: VehicleNodes, validVehicleNodes: string[] = DEFAULT_VEHICLE_NODES): Date | null {
  const vehicleNodeDate = (vehicleNodes as Record<string, string | undefined>)[nodeName];
  if (vehicleNodeDate !== undefined && vehicleNodeDate !== null && vehicleNodeDate !== '') {
    return new Date(vehicleNodeDate);
  }

  const pattern = new RegExp(`^(${validVehicleNodes.join('|')})([+-]\\d+)周?$`);
  const match = nodeName.match(pattern);
  if (match) {
    const baseNode = match[1];
    const offsetWeeks = parseInt(match[2]);
    const baseDate = (vehicleNodes as Record<string, string | undefined>)[baseNode];
    if (baseDate !== undefined && baseDate !== null && baseDate !== '') {
      return addWeeks(new Date(baseDate), offsetWeeks);
    }
  }

  return null;
}

function isVehicleNode(nodeName: string, validVehicleNodes: string[] = DEFAULT_VEHICLE_NODES): boolean {
  if (!nodeName) return false;
  if (validVehicleNodes.includes(nodeName)) {
    return true;
  }
  const pattern = new RegExp(`^(${validVehicleNodes.join('|')})([+-]\\d+)周?$`);
  return !!pattern.test(nodeName);
}

interface TaskResult {
  start: Date | null;
  end: Date | null;
  duration: number;
  isNaDuration: boolean;
}

interface SubTaskGroup {
  name: string;
  tasks: Array<TaskTemplate & { phase: string }>;
}

function getDuration(task: TaskTemplate, mode: 'standard' | 'minDuration' | 'syncDuration'): number {
  if (task.duration === null || task.duration === undefined) {
    return 0;
  }
  
  if (mode === 'minDuration' || mode === 'syncDuration') {
    return task.minDuration || task.duration;
  }
  
  return task.duration;
}

function checkMinDurationConditions(
  tasks: any[], 
  vehicleNodes: VehicleNodes,
  functionalNodeMapping: Record<string, string>
): boolean {
  // 从映射中获取功能节点对应的实际整车节点名称
  const firstVehicleNode = functionalNodeMapping['首次造车'] || '首次造车';
  const salesVehicleNode = functionalNodeMapping['可销售车'] || '可销售车';
  
  // 获取功能节点的日期
  const firstVehicleDate = vehicleNodes[firstVehicleNode as keyof VehicleNodes] 
    ? new Date(vehicleNodes[firstVehicleNode as keyof VehicleNodes] as string) 
    : null;
  const salesVehicleDate = vehicleNodes[salesVehicleNode as keyof VehicleNodes] 
    ? new Date(vehicleNodes[salesVehicleNode as keyof VehicleNodes] as string) 
    : null;
  const salesVehicleMinus2Weeks = salesVehicleDate ? addWeeks(salesVehicleDate, -2) : null;

  // 判定项1：功能任务"产品T0"晚于功能节点"首次造车"
  const t0Task = tasks.find(t => t.functionalTask === '产品T0' || t.name === '总成T0');
  if (t0Task && firstVehicleDate && new Date(t0Task.end) > firstVehicleDate) {
    return true;
  }

  // 判定项2：功能任务"皮纹加工"晚于功能节点"可销售车"-2周
  const skinTextureTask = tasks.find(t => t.functionalTask === '皮纹加工' || t.name === '皮纹加工');
  if (skinTextureTask && salesVehicleMinus2Weeks && new Date(skinTextureTask.end) > salesVehicleMinus2Weeks) {
    return true;
  }

  // 判定项3：功能任务"电子试验"晚于功能节点"可销售车"-2周
  const eeTestTask = tasks.find(t => t.functionalTask === '电子试验' || t.name === 'EMC/3172');
  if (eeTestTask && salesVehicleMinus2Weeks && new Date(eeTestTask.end) > salesVehicleMinus2Weeks) {
    return true;
  }

  // 判定项4：功能任务"产品试验"晚于功能节点"可销售车"-2周
  const lampTestTask = tasks.find(t => t.functionalTask === '产品试验' || t.name === 'GMW14906');
  if (lampTestTask && salesVehicleMinus2Weeks && new Date(lampTestTask.end) > salesVehicleMinus2Weeks) {
    return true;
  }

  // 判定项5：功能任务"法规认证"晚于功能节点"可销售车"-2周
  const regulationTask = tasks.find(t => t.functionalTask === '法规认证' || t.name === '强检/CCC');
  if (regulationTask && salesVehicleMinus2Weeks && new Date(regulationTask.end) > salesVehicleMinus2Weeks) {
    return true;
  }

  return false;
}

function generateTasksWithMode(
  template: PhaseTemplate[],
  vehicleNodes: VehicleNodes,
  mode: 'standard' | 'minDuration' | 'syncDuration',
  validVehicleNodes: string[] = DEFAULT_VEHICLE_NODES
): any[] {
  const allTasks: Array<TaskTemplate & { phase: string }> = [];
  const subTaskGroups = new Map<string, SubTaskGroup>();
  
  template.forEach((phase) => {
    phase.tasks.forEach((task) => {
      const taskWithPhase = { ...task, phase: phase.phase };
      allTasks.push(taskWithPhase);
      
      const subTaskName = task.subTask || '';
      if (!subTaskGroups.has(subTaskName)) {
        subTaskGroups.set(subTaskName, { name: subTaskName, tasks: [] });
      }
      subTaskGroups.get(subTaskName)!.tasks.push(taskWithPhase);
    });
  });

  const results = new Map<string, TaskResult>();
  allTasks.forEach((task) => {
    const duration = getDuration(task, mode);
    const isNaDuration = task.duration === null || task.duration === undefined;
    results.set(task.name, {
      start: null,
      end: null,
      duration,
      isNaDuration,
    });
  });

  // 处理引用vehicle节点的任务
  const anchoredTasks = new Set<string>();
  allTasks.forEach((task) => {
    const result = results.get(task.name)!;
    
    // 处理startNode引用vehicle节点
    if (task.startNode && isVehicleNode(task.startNode, validVehicleNodes)) {
      const parsedStart = parseVehicleNode(task.startNode, vehicleNodes, validVehicleNodes);
      if (parsedStart) {
        let startDate = parsedStart;
        const startOffset = (task as any).startNodeOffset ?? task.offsetWeeks;
        if (startOffset) {
          startDate = addWeeks(startDate, startOffset);
        }
        result.start = startDate;
        if (!result.isNaDuration && result.duration > 0) {
          result.end = addDays(startDate, result.duration);
        }
        anchoredTasks.add(task.name);
      }
    }
    
    // 处理endNode引用vehicle节点
    if (task.endNode && isVehicleNode(task.endNode, validVehicleNodes)) {
      const parsedEnd = parseVehicleNode(task.endNode, vehicleNodes, validVehicleNodes);
      if (parsedEnd) {
        let endDate = parsedEnd;
        const endOffset = (task as any).endNodeOffset ?? task.offsetWeeks;
        if (endOffset) {
          endDate = addWeeks(endDate, endOffset);
        }
        result.end = endDate;
        if (!result.isNaDuration && result.duration > 0) {
          result.start = addDays(endDate, -result.duration);
        }
        anchoredTasks.add(task.name);
      }
    }
  });

  // 迭代处理依赖关系
  let hasChanges = true;
  let iterations = 0;
  const maxIterations = 50;
  
  while (hasChanges && iterations < maxIterations) {
    hasChanges = false;
    iterations++;
    
    allTasks.forEach((task) => {
      const result = results.get(task.name)!;
      
      // 处理startNode引用其他任务
      if (task.startNode && !isVehicleNode(task.startNode, validVehicleNodes)) {
        const refResult = results.get(task.startNode);
        if (refResult && refResult.end) {
          let startDate = new Date(refResult.end);
          const startOffset = (task as any).startNodeOffset ?? task.offsetWeeks;
          if (startOffset) {
            startDate = addWeeks(startDate, startOffset);
          }
          if (!result.start || startDate > result.start) {
            result.start = startDate;
            if (!result.isNaDuration && result.duration > 0) {
              result.end = addDays(startDate, result.duration);
            }
            hasChanges = true;
          }
        }
      }
      
      // 处理endNode引用其他任务
      if (task.endNode && !isVehicleNode(task.endNode, validVehicleNodes)) {
        const refResult = results.get(task.endNode);
        if (refResult && refResult.end) {
          let endDate = new Date(refResult.end);
          const endOffset = (task as any).endNodeOffset ?? task.offsetWeeks;
          if (endOffset) {
            endDate = addWeeks(endDate, endOffset);
          }
          if (!result.end || endDate < result.end) {
            result.end = endDate;
            if (!result.isNaDuration && result.duration > 0) {
              result.start = addDays(endDate, -result.duration);
            }
            hasChanges = true;
          }
        }
      }
      
      // 处理predecessor引用
      if (task.predecessor) {
        const predResult = results.get(task.predecessor);
        if (predResult && predResult.end) {
          let startDate = new Date(predResult.end);
          
          // 同步工期模式：应用overlap
          if (mode === 'syncDuration' && task.overlapWeeks) {
            startDate = addWeeks(startDate, -task.overlapWeeks);
          }
          
          if (!result.start || startDate > result.start) {
            result.start = startDate;
            if (!result.isNaDuration && result.duration > 0) {
              result.end = addDays(startDate, result.duration);
            }
            hasChanges = true;
          }
        }
      }
    });
  }

  // 处理NA工期任务
  allTasks.forEach((task) => {
    const result = results.get(task.name)!;
    if (result.isNaDuration && result.start && !result.end) {
      // 查找下一个任务的开始时间
      for (const otherTask of allTasks) {
        if (otherTask.predecessor === task.name || otherTask.startNode === task.name) {
          const otherResult = results.get(otherTask.name)!;
          if (otherResult.start) {
            result.end = new Date(otherResult.start);
            result.duration = Math.max(1, Math.round((result.end.getTime() - result.start.getTime()) / (24 * 60 * 60 * 1000)));
            break;
          }
        }
      }
    }
  });

  // 倒排处理：对于有固定结束时间的任务，向前倒排前序任务链
  allTasks.forEach((task) => {
    const result = results.get(task.name)!;
    
    // 如果任务有固定的结束时间（通过endNode引用vehicle节点）
    if (task.endNode && isVehicleNode(task.endNode, validVehicleNodes) && result.end && result.start) {
      // 向前倒排前序任务链
      let currentTask: TaskTemplate | undefined = task;
      let currentEnd = new Date(result.start); // 前序任务的结束时间 = 当前任务的开始时间
      
      while (currentTask && currentEnd) {
        // 找到当前任务的前序任务
        const predName = currentTask.predecessor;
        if (!predName) break;
        
        const predTask = allTasks.find(t => t.name === predName);
        if (!predTask) break;
        
        const predResult = results.get(predName)!;
        
        // 如果前序任务已经有固定的开始时间
        if (predTask.startNode && isVehicleNode(predTask.startNode, validVehicleNodes) && predResult.start) {
          // 对于NA工期任务，计算结束时间
          if (predResult.isNaDuration) {
            predResult.end = new Date(currentEnd);
            predResult.duration = Math.max(1, Math.round((predResult.end.getTime() - predResult.start.getTime()) / (24 * 60 * 60 * 1000)));
          }
          break; // 停止倒排
        }
        
        // 倒排前序任务
        predResult.end = new Date(currentEnd);
        if (!predResult.isNaDuration && predResult.duration > 0) {
          predResult.start = addDays(predResult.end, -predResult.duration);
          currentEnd = new Date(predResult.start);
          currentTask = predTask;
        } else if (predResult.isNaDuration) {
          // NA工期：需要找到前前序任务的结束时间
          const predPredName = predTask.predecessor;
          if (predPredName) {
            const predPredResult = results.get(predPredName);
            if (predPredResult && predPredResult.end) {
              predResult.start = new Date(predPredResult.end);
              predResult.duration = Math.max(1, Math.round((predResult.end.getTime() - predResult.start.getTime()) / (24 * 60 * 60 * 1000)));
              currentEnd = new Date(predResult.start);
              currentTask = predTask;
            } else {
              break;
            }
          } else {
            break;
          }
        } else {
          break;
        }
      }
    }
  });

  // 处理每个subTask组内未确定的任务
  subTaskGroups.forEach((group) => {
    // 找出组内有固定时间的任务作为锚点
    const anchorIndices: number[] = [];
    group.tasks.forEach((task, index) => {
      const result = results.get(task.name)!;
      if (result.start && result.end) {
        anchorIndices.push(index);
      }
    });

    // 如果没有锚点，检查任务是否引用了其他任务
    if (anchorIndices.length === 0) {
      let currentStart: Date | null = null;
      
      // 查找第一个任务是否引用了其他任务
      const firstTask = group.tasks[0];
      if (firstTask.startNode) {
        const refResult = results.get(firstTask.startNode);
        if (refResult && refResult.end) {
          currentStart = new Date(refResult.end);
        }
      } else if (firstTask.predecessor) {
        const predResult = results.get(firstTask.predecessor);
        if (predResult && predResult.end) {
          currentStart = new Date(predResult.end);
        }
      }
      
      // 如果没有找到引用，则从第一个有效的节点开始（按优先级顺序尝试）
      if (!currentStart) {
        const fallbackOrder = ['VDR', 'SORP', 'S', 'APPV', 'VPI'];
        for (const nodeName of fallbackOrder) {
          if (vehicleNodes[nodeName]) {
            currentStart = new Date(vehicleNodes[nodeName]);
            break;
          }
        }
        // 尝试用户自定义节点
        if (!currentStart) {
          for (const nodeName of validVehicleNodes) {
            if (vehicleNodes[nodeName]) {
              currentStart = new Date(vehicleNodes[nodeName]);
              break;
            }
          }
        }
      }
      
      group.tasks.forEach((task) => {
        const result = results.get(task.name)!;
        if (currentStart) {
          // 如果任务已经有开始日期（通过之前的引用处理），则使用已有的
          if (!result.start) {
            result.start = new Date(currentStart);
          }
          if (result.isNaDuration) {
            result.duration = 14;
          }
          if (result.duration > 0 && !result.end) {
            result.end = addDays(result.start, result.duration);
            currentStart = new Date(result.end);
          } else if (result.end) {
            currentStart = new Date(result.end);
          }
        }
      });
      return;
    }

    // 处理第一个锚点之前的任务（正排）
    if (anchorIndices[0] > 0) {
      const firstAnchor = group.tasks[anchorIndices[0]];
      const firstAnchorResult = results.get(firstAnchor.name)!;
      let currentEnd = new Date(firstAnchorResult.start!);
      
      // 倒排第一个锚点之前的任务
      for (let i = anchorIndices[0] - 1; i >= 0; i--) {
        const task = group.tasks[i];
        const result = results.get(task.name)!;
        
        result.end = new Date(currentEnd);
        if (result.isNaDuration) {
          result.duration = 14;
        }
        result.start = addDays(result.end, -result.duration);
        currentEnd = new Date(result.start);
      }
    }

    // 处理锚点之间的任务
    for (let i = 0; i < anchorIndices.length; i++) {
      const currentAnchorIdx = anchorIndices[i];
      const nextAnchorIdx = anchorIndices[i + 1];
      
      if (nextAnchorIdx !== undefined && nextAnchorIdx > currentAnchorIdx + 1) {
        // 有下一个锚点，且中间有任务，需要倒排
        const nextAnchor = group.tasks[nextAnchorIdx];
        const nextAnchorResult = results.get(nextAnchor.name)!;
        let currentEnd = new Date(nextAnchorResult.start!);
        
        for (let j = nextAnchorIdx - 1; j > currentAnchorIdx; j--) {
          const task = group.tasks[j];
          const result = results.get(task.name)!;
          
          result.end = new Date(currentEnd);
          if (result.isNaDuration) {
            result.duration = 14;
          }
          result.start = addDays(result.end, -result.duration);
          currentEnd = new Date(result.start);
        }
      } else if (nextAnchorIdx === undefined) {
        // 没有下一个锚点，正排剩余任务
        const lastAnchor = group.tasks[currentAnchorIdx];
        const lastAnchorResult = results.get(lastAnchor.name)!;
        let currentStart = new Date(lastAnchorResult.end!);
        
        for (let j = currentAnchorIdx + 1; j < group.tasks.length; j++) {
          const task = group.tasks[j];
          const result = results.get(task.name)!;
          
          result.start = new Date(currentStart);
          if (result.isNaDuration) {
            result.duration = 14;
          }
          result.end = addDays(result.start, result.duration);
          currentStart = new Date(result.end);
        }
      }
    }
  });

  // 生成最终任务列表
  const finalTasks: any[] = [];
  template.forEach((phase) => {
    phase.tasks.forEach((task) => {
      const result = results.get(task.name);
      if (result) {
        finalTasks.push({
          id: task.name.replace(/\s+/g, '_').toLowerCase().replace(/[^a-z0-9_]/g, ''),
          name: task.name,
          phase: phase.phase,
          subTask: task.subTask || '',
          start: result.start ? result.start.toISOString().split('T')[0] : '',
          end: result.end ? result.end.toISOString().split('T')[0] : '',
          duration: result.duration || 0,
          owner: '',
          status: 'not-started',
          description: task.description,
          predecessor: task.predecessor,
          startNode: task.startNode,
          functionalTask: task.functionalTask,
          reviewTask: task.reviewTask,
          reviewAttachments: [],
        });
      }
    });
  });

  return finalTasks;
}

export function calculateTaskDates(
  template: PhaseTemplate[],
  vehicleNodes: VehicleNodes,
  functionalNodeMapping: Record<string, string>,
  forceMinDuration: boolean = false,
  customVehicleNodes: string[] = []
): any[] {
  const validVehicleNodes = [...DEFAULT_VEHICLE_NODES, ...customVehicleNodes];
  
  // 第一步：生成标准工期
  const standardTasks = generateTasksWithMode(template, vehicleNodes, 'standard', validVehicleNodes);

  // 第二步：检查是否需要最短工期
  const needMinDuration = forceMinDuration || checkMinDurationConditions(standardTasks, vehicleNodes, functionalNodeMapping);

  if (needMinDuration) {
    // 第三步：使用最短工期重新生成
    const minDurationTasks = generateTasksWithMode(template, vehicleNodes, 'minDuration', validVehicleNodes);
    
    // 第四步：检查是否需要同步工期
    const needSyncDuration = checkMinDurationConditions(minDurationTasks, vehicleNodes, functionalNodeMapping);
    
    if (needSyncDuration) {
      return generateTasksWithMode(template, vehicleNodes, 'syncDuration', validVehicleNodes);
    }
    
    return minDurationTasks;
  }

  return standardTasks;
}

type FunctionalNode = '项目立项' | '数据发布' | '首次造车' | '可销售车';

interface MilestoneItem {
  id: string;
  sequence: number;
  name: string;
  description: string;
  required: boolean;
  functionalNode: FunctionalNode | 'N/A';
}

interface VehicleMilestoneTemplate {
  id: string;
  oem: string;
  description: string;
  milestones: MilestoneItem[];
  created_at: string;
  updated_at: string;
}

export function extractFunctionalNodeMapping(
  vehicleTemplate: VehicleMilestoneTemplate | null | undefined
): Record<FunctionalNode, string> {
  const mapping: Record<FunctionalNode, string> = {
    '项目立项': 'VPI',
    '数据发布': 'VDR',
    '首次造车': 'APPV',
    '可销售车': 'S'
  };

  if (!vehicleTemplate || !vehicleTemplate.milestones) {
    return mapping;
  }

  vehicleTemplate.milestones.forEach((milestone) => {
    if (milestone.functionalNode && milestone.functionalNode !== 'N/A') {
      mapping[milestone.functionalNode] = milestone.name;
    }
  });

  return mapping;
}

export function extractCustomVehicleNodes(
  vehicleTemplate: VehicleMilestoneTemplate | null | undefined
): string[] {
  const defaultNodes = new Set(DEFAULT_VEHICLE_NODES);
  const customNodes: string[] = [];

  if (!vehicleTemplate || !vehicleTemplate.milestones) {
    return customNodes;
  }

  vehicleTemplate.milestones.forEach((milestone) => {
    if (milestone.name && !defaultNodes.has(milestone.name)) {
      customNodes.push(milestone.name);
    }
  });

  return customNodes;
}

export function replaceFunctionalNodesInTemplate(
  template: PhaseTemplate[],
  mapping: Record<FunctionalNode, string>
): PhaseTemplate[] {
  const functionalNodes: FunctionalNode[] = ['项目立项', '数据发布', '首次造车', '可销售车'];
  
  const deepCopy = JSON.parse(JSON.stringify(template)) as PhaseTemplate[];
  
  deepCopy.forEach((phase) => {
    phase.tasks.forEach((task) => {
      if (task.startNode && functionalNodes.includes(task.startNode as FunctionalNode)) {
        task.startNode = mapping[task.startNode as FunctionalNode];
      }
      if (task.endNode && functionalNodes.includes(task.endNode as FunctionalNode)) {
        task.endNode = mapping[task.endNode as FunctionalNode];
      }
    });
  });

  return deepCopy;
}

export function extractFunctionalTaskMapping(
  template: PhaseTemplate[] | undefined
): Partial<Record<FunctionalTask, string>> {
  const mapping: Partial<Record<FunctionalTask, string>> = {};

  if (!template) {
    return mapping;
  }

  template.forEach((phase) => {
    phase.tasks.forEach((task) => {
      if (task.functionalTask && task.functionalTask !== '-') {
        mapping[task.functionalTask] = task.name;
      }
    });
  });

  return mapping;
}

export function extractFunctionalTaskMappingFromTasks(
  tasks: { name: string; functionalTask?: FunctionalTask }[]
): Partial<Record<FunctionalTask, string>> {
  const mapping: Partial<Record<FunctionalTask, string>> = {};

  tasks.forEach((task) => {
    if (task.functionalTask && task.functionalTask !== '-') {
      mapping[task.functionalTask] = task.name;
    }
  });

  return mapping;
}
